import React, { useState, useRef, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import { codeSnippetAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import AIChatAssistant from '../components/AIChatAssistant';
import SmartToyIcon from '@mui/icons-material/SmartToy';

const LANGUAGES = [
  { value: 'javascript', label: 'JavaScript' },
  { value: 'typescript', label: 'TypeScript' },
  { value: 'python', label: 'Python' },
  { value: 'java', label: 'Java' },
  { value: 'html', label: 'HTML' },
  { value: 'css', label: 'CSS' },
  { value: 'sql', label: 'SQL' },
  { value: 'csharp', label: 'C#' },
  { value: 'cpp', label: 'C++' },
  { value: 'php', label: 'PHP' },
  { value: 'ruby', label: 'Ruby' },
  { value: 'go', label: 'Go' },
  { value: 'rust', label: 'Rust' },
  { value: 'kotlin', label: 'Kotlin' },
  { value: 'swift', label: 'Swift' }
];

function LiveCodeEditor() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [language, setLanguage] = useState('javascript');
  const [explanations, setExplanations] = useState({});
  const [generating, setGenerating] = useState(false);
  const [lastLineCount, setLastLineCount] = useState(0);
  const [error, setError] = useState('');
  const [theme, setTheme] = useState('vs-dark');
  const [autoExplain, setAutoExplain] = useState(true);
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const debounceTimerRef = useRef(null);
  const editorRef = useRef(null);

  // Redirect if not admin
  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/');
    }
  }, [user, navigate]);

  const getCommentStyle = (lang) => {
    switch (lang) {
      case 'python':
      case 'ruby':
        return { single: '#', multiStart: '"""', multiEnd: '"""' };
      case 'html':
        return { single: '<!--', multiStart: '<!--', multiEnd: '-->' };
      case 'css':
        return { single: '/*', multiStart: '/*', multiEnd: '*/' };
      default:
        return { single: '//', multiStart: '/*', multiEnd: '*/' };
    }
  };

  const formatExplanationAsComment = (explanation, lang) => {
    const commentStyle = getCommentStyle(lang);
    return `${commentStyle.single} ${explanation}`;
  };

  const generateCodeWithComments = () => {
    if (!code) return '';
    
    const lines = code.split('\n');
    const result = [];
    
    lines.forEach((line, index) => {
      const lineNumber = index + 1;
      const explanation = explanations[lineNumber];
      
      if (explanation && line.trim()) {
        result.push(formatExplanationAsComment(explanation, language));
      }
      result.push(line);
    });
    
    return result.join('\n');
  };

  const handleCodeChange = (value) => {
    setCode(value || '');
    
    if (!autoExplain) return;

    const lines = (value || '').split('\n');
    const currentLineCount = lines.length;

    // Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Only generate explanations if code has increased
    if (currentLineCount > lastLineCount) {
      // Debounce the explanation generation - wait for user to stop typing
      debounceTimerRef.current = setTimeout(() => {
        handleGenerateExplanations(value);
      }, 1500); // Wait 1.5 seconds after user stops typing
    }

    setLastLineCount(currentLineCount);
  };

  const handleGenerateExplanations = async (codeToExplain = code) => {
    if (!codeToExplain || !codeToExplain.trim()) {
      return;
    }

    setGenerating(true);
    setError('');

    try {
      const response = await codeSnippetAPI.explainCode({
        code: codeToExplain,
        language,
        snippetName: 'Live Code'
      });

      const newExplanations = {};
      response.data.explanations.forEach(exp => {
        newExplanations[exp.lineNumber] = exp.explanation;
      });

      setExplanations(newExplanations);
    } catch (err) {
      console.error('Error generating explanations:', err);
      setError(err.response?.data?.message || 'Failed to generate explanations');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyWithComments = () => {
    const codeWithComments = generateCodeWithComments();
    navigator.clipboard.writeText(codeWithComments);
    alert('Code with explanations copied to clipboard!');
  };

  const handleClearAll = () => {
    if (window.confirm('Clear all code and explanations?')) {
      setCode('');
      setExplanations({});
      setLastLineCount(0);
      setError('');
    }
  };

  const editorOptions = {
    minimap: { enabled: true },
    fontSize: 16,
    lineNumbers: 'on',
    scrollBeyondLastLine: false,
    automaticLayout: true,
    tabSize: 2,
    wordWrap: 'on',
    formatOnPaste: true,
    formatOnType: true
  };

  if (user && user.role !== 'admin') {
    return null;
  }

  return (
    <div className="container" style={{ maxWidth: '1400px', padding: '20px' }}>
      <div className="card" style={{ padding: '30px' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ 
            fontSize: '28px', 
            fontWeight: '700', 
            color: '#2d3748', 
            margin: '0 0 8px 0' 
          }}>
            💻 Live Code Editor with AI Explanations
          </h2>
          <p style={{ 
            fontSize: '14px', 
            color: '#718096', 
            margin: 0 
          }}>
            Write code and get line-by-line explanations automatically generated as you type
          </p>
        </div>

        {/* Controls */}
        <div style={{ 
          display: 'flex', 
          gap: '16px', 
          marginBottom: '20px',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '6px', 
              fontSize: '13px', 
              fontWeight: '500',
              color: '#4a5568'
            }}>
              Language
            </label>
            <select
              value={language}
              onChange={(e) => {
                setLanguage(e.target.value);
                setExplanations({});
              }}
              style={{
                padding: '8px 12px',
                border: '1.5px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px',
                backgroundColor: 'white',
                minWidth: '150px'
              }}
            >
              {LANGUAGES.map(lang => (
                <option key={lang.value} value={lang.value}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '24px' }}>
            <input
              type="checkbox"
              id="autoExplain"
              checked={autoExplain}
              onChange={(e) => setAutoExplain(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <label 
              htmlFor="autoExplain" 
              style={{ 
                fontSize: '13px', 
                color: '#4a5568',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              Auto-generate explanations
            </label>
          </div>

          <div style={{ marginLeft: 'auto', marginTop: '24px', display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setIsChatbotOpen(!isChatbotOpen)}
              className="btn btn-secondary"
              style={{ 
                fontSize: '13px', 
                padding: '8px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                border: isChatbotOpen ? '2px solid #667eea' : undefined,
                backgroundColor: isChatbotOpen ? '#667eea' : undefined,
                color: isChatbotOpen ? 'white' : undefined
              }}
              title="Ask AI about your code (Ctrl+Shift+O)"
            >
              <SmartToyIcon fontSize="small" />
              {isChatbotOpen ? 'Close AI Chat' : 'AI Chat'}
            </button>

            <button
              onClick={() => setTheme(theme === 'vs-dark' ? 'light' : 'vs-dark')}
              className="btn btn-secondary"
              style={{ fontSize: '13px', padding: '8px 16px' }}
            >
              {theme === 'vs-dark' ? '☀️ Light' : '🌙 Dark'}
            </button>

            {!autoExplain && (
              <button
                onClick={() => handleGenerateExplanations()}
                disabled={generating || !code.trim()}
                className="btn btn-primary"
                style={{ 
                  fontSize: '13px', 
                  padding: '8px 16px',
                  opacity: (generating || !code.trim()) ? 0.6 : 1
                }}
              >
                {generating ? '🔄 Generating...' : '✨ Generate Explanations'}
              </button>
            )}

            <button
              onClick={handleCopyWithComments}
              disabled={Object.keys(explanations).length === 0}
              className="btn btn-secondary"
              style={{ 
                fontSize: '13px', 
                padding: '8px 16px',
                opacity: Object.keys(explanations).length === 0 ? 0.6 : 1
              }}
            >
              📋 Copy with Comments
            </button>

            <button
              onClick={handleClearAll}
              disabled={!code.trim()}
              className="btn btn-secondary"
              style={{ 
                fontSize: '13px', 
                padding: '8px 16px',
                opacity: !code.trim() ? 0.6 : 1
              }}
            >
              🗑️ Clear
            </button>
          </div>
        </div>

        {/* Status */}
        <div style={{ marginBottom: '16px', minHeight: '24px' }}>
          {generating && (
            <div style={{
              padding: '8px 12px',
              backgroundColor: '#eef2ff',
              border: '1px solid #c7d2fe',
              borderRadius: '6px',
              color: '#4338ca',
              fontSize: '13px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span className="spinner"></span>
              Generating explanations...
            </div>
          )}

          {!generating && Object.keys(explanations).length > 0 && (
            <div style={{
              padding: '8px 12px',
              backgroundColor: '#d1fae5',
              border: '1px solid #6ee7b7',
              borderRadius: '6px',
              color: '#065f46',
              fontSize: '13px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              ✅ {Object.keys(explanations).length} line(s) explained
            </div>
          )}

          {error && (
            <div style={{
              padding: '8px 12px',
              backgroundColor: '#fff5f5',
              border: '1px solid #feb2b2',
              borderRadius: '6px',
              color: '#c53030',
              fontSize: '13px'
            }}>
              ❌ {error}
            </div>
          )}
        </div>

        {/* Editor */}
        <div style={{ 
          border: '1px solid #e2e8f0', 
          borderRadius: '8px', 
          overflow: 'hidden',
          marginBottom: '20px'
        }}>
          <Editor
            height="500px"
            language={language}
            value={code}
            onChange={handleCodeChange}
            theme={theme}
            options={editorOptions}
            onMount={(editor) => {
              editorRef.current = editor;
            }}
          />
        </div>

        {/* Line-by-Line Explanations */}
        {Object.keys(explanations).length > 0 && (
          <div style={{ marginTop: '24px' }}>
            <h3 style={{ 
              fontSize: '18px', 
              fontWeight: '600', 
              color: '#2d3748', 
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              💡 Line-by-Line Explanations
            </h3>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '12px' 
            }}>
              {code.split('\n').map((line, index) => {
                const lineNumber = index + 1;
                const explanation = explanations[lineNumber];
                
                if (!explanation || !line.trim()) {
                  return null;
                }

                return (
                  <div 
                    key={lineNumber}
                    style={{
                      background: '#f7fafc',
                      border: '1px solid #e2e8f0',
                      borderLeft: '4px solid #667eea',
                      borderRadius: '8px',
                      padding: '16px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#eef2ff';
                      e.currentTarget.style.borderLeftColor = '#4338ca';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f7fafc';
                      e.currentTarget.style.borderLeftColor = '#667eea';
                    }}
                  >
                    {/* Line Number and Code */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start',
                      gap: '12px',
                      marginBottom: '12px'
                    }}>
                      <div style={{
                        minWidth: '40px',
                        height: '32px',
                        borderRadius: '6px',
                        background: '#667eea',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '700',
                        fontSize: '14px',
                        flexShrink: 0
                      }}>
                        {lineNumber}
                      </div>
                      <div style={{
                        flex: 1,
                        background: theme === 'vs-dark' ? '#1e1e1e' : '#2d3748',
                        color: theme === 'vs-dark' ? '#d4d4d4' : '#f7fafc',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        fontFamily: 'monospace',
                        fontSize: '14px',
                        overflow: 'auto',
                        lineHeight: '1.5'
                      }}>
                        {line}
                      </div>
                    </div>

                    {/* Explanation */}
                    <div style={{
                      paddingLeft: '52px',
                      color: '#4a5568',
                      fontSize: '14px',
                      lineHeight: '1.7'
                    }}>
                      <div style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#667eea',
                        marginBottom: '6px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Explanation
                      </div>
                      {explanation}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Copy with Comments Button */}
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <button
                onClick={handleCopyWithComments}
                className="btn btn-secondary"
                style={{ 
                  fontSize: '14px', 
                  padding: '10px 20px'
                }}
              >
                📋 Copy Code with Embedded Comments
              </button>
            </div>
          </div>
        )}

        {/* Usage Tips */}
        <div style={{
          marginTop: '24px',
          padding: '16px',
          background: '#f7fafc',
          borderLeft: '4px solid #667eea',
          borderRadius: '6px'
        }}>
          <h4 style={{ 
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#4338ca', 
            margin: '0 0 8px 0' 
          }}>
            💡 Tips
          </h4>
          <ul style={{ 
            margin: 0, 
            paddingLeft: '20px',
            fontSize: '13px',
            color: '#4a5568'
          }}>
            <li>Enable "Auto-generate explanations" to get explanations as you type</li>
            <li>Explanations are generated after you stop typing for 1.5 seconds</li>
            <li>Each line of code gets its own explanation as a comment</li>
            <li>Use "Copy with Comments" to copy code with embedded explanations</li>
            <li>Change the language to adjust comment style automatically</li>
          </ul>
        </div>
      </div>

      <style>{`
        .spinner {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 2px solid #c7d2fe;
          border-top-color: #4338ca;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* AI Chat Assistant */}
      <AIChatAssistant 
        isDarkMode={theme === 'vs-dark'}
        isOpen={isChatbotOpen}
        onOpenChange={setIsChatbotOpen}
        assignmentContext={{
          code: code,
          language: language,
          explanations: Object.entries(explanations).map(([lineNumber, explanation]) => ({
            lineNumber: parseInt(lineNumber),
            explanation: explanation
          }))
        }}
      />
    </div>
  );
}

export default LiveCodeEditor;
